import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import type { Site } from '@totum-analytics/db';
import { Badge, Button, Card, Empty, ErrorNote, Field, Loading, inputClass } from '../components/ui';
import { post } from '../lib/api';
import { formatDay, formatRelative } from '../lib/format';
import { useApi } from '../lib/useApi';

export function siteStatus(site: Site): { tone: 'green' | 'yellow' | 'gray' | 'red'; label: string } {
  if (site.deletion_scheduled_for) return { tone: 'red', label: `Deletion on ${formatDay(site.deletion_scheduled_for.slice(0, 10))}` };
  if (!site.is_active) return { tone: 'gray', label: 'Paused' };
  if (!site.last_event_at) return { tone: 'yellow', label: 'Waiting for first event' };
  if (site.identity_mode === 'identified' && !site.last_identified_at) return { tone: 'yellow', label: 'No identified users yet' };
  return { tone: 'green', label: 'Receiving' };
}

export function ModeBadge({ mode }: { mode: Site['identity_mode'] }) {
  return mode === 'identified' ? <Badge tone="blue" icon="●">Identified</Badge> : <Badge tone="gray" icon="○">Anonymous</Badge>;
}

function AddSite({ onCancel }: { onCancel: () => void }) {
  const navigate = useNavigate();
  const [error, setError] = useState<{ message: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    try {
      const { site } = await post<{ site: Site }>('/sites', {
        name: String(form.get('name')).trim(),
        hostnames: String(form.get('hostnames')).split(/[\s,]+/).filter(Boolean),
        identity_mode: form.get('identity_mode'),
      });
      navigate(`/sites/${site.id}/install`);
    } catch (err) {
      setError(err as Error);
      setBusy(false);
    }
  }

  return (
    <Card title="Add a site" className="mb-6">
      <form onSubmit={submit} className="grid gap-x-6 md:grid-cols-2">
        <div>
          <Field label="Name" htmlFor="name" hint="What people at Inovus call it.">
            <input id="name" name="name" required maxLength={120} placeholder="Totum Assess" className={inputClass} />
          </Field>
          <Field label="Hostnames" htmlFor="hostnames" hint="Where the tag will run. Separate several with spaces or commas; *.example.com covers subdomains.">
            <input id="hostnames" name="hostnames" required placeholder="assess.totum.surgery" className={inputClass} />
          </Field>
        </div>
        <fieldset className="mb-4">
          <legend className="block text-sm font-medium text-gray-700 mb-2">Who uses it</legend>
          <label className="flex gap-3 rounded-lg border border-gray-200 p-3 mb-2 cursor-pointer has-[:checked]:border-totum-blue has-[:checked]:bg-totum-blue/5">
            <input type="radio" name="identity_mode" value="identified" defaultChecked className="mt-1 accent-totum-blue" />
            <span>
              <span className="block font-medium text-gray-900">Signed-in users — identified</span>
              <span className="block text-sm text-gray-600">A Totum application behind a login. Usage is attributed to named people and customer accounts.</span>
            </span>
          </label>
          <label className="flex gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer has-[:checked]:border-totum-blue has-[:checked]:bg-totum-blue/5">
            <input type="radio" name="identity_mode" value="anonymous" className="mt-1 accent-totum-blue" />
            <span>
              <span className="block font-medium text-gray-900">The public — anonymous</span>
              <span className="block text-sm text-gray-600">A marketing or public site. Traffic only; identities are never collected.</span>
            </span>
          </label>
        </fieldset>
        <div className="md:col-span-2 flex items-center gap-3 flex-wrap">
          <Button type="submit" disabled={busy}>{busy ? 'Adding…' : 'Add site and get the tag'}</Button>
          <Button kind="quiet" onClick={onCancel}>Cancel</Button>
          {error && <div className="w-full mt-3"><ErrorNote error={error} /></div>}
        </div>
      </form>
    </Card>
  );
}

export function Sites() {
  const { data, error, loading, reload } = useApi<Site[]>('/sites', { pollMs: 30_000 });
  const [adding, setAdding] = useState(false);

  return (
    <>
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sites</h1>
          <p className="text-gray-600">Every application and website sending analytics.</p>
        </div>
        {!adding && <Button onClick={() => setAdding(true)}>Add a site</Button>}
      </div>

      {adding && <AddSite onCancel={() => setAdding(false)} />}
      {error && <ErrorNote error={error} onRetry={reload} />}
      {loading && !data && <Loading label="Loading sites" />}

      {data && data.length === 0 && !adding && (
        <Card>
          <Empty title="No sites yet">Add one to get a tag to paste into it. Data starts arriving within seconds.</Empty>
        </Card>
      )}

      {data && data.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          {data.map((site) => {
            const status = siteStatus(site);
            return (
              <Link
                key={site.id}
                to={`/sites/${site.id}`}
                className="block bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-3">
                  <h2 className="text-lg font-semibold text-gray-900">{site.name}</h2>
                  <ModeBadge mode={site.identity_mode} />
                </div>
                <p className="mt-1 text-sm text-gray-600 truncate">{site.hostnames.join(', ')}</p>
                <div className="mt-4 flex items-center justify-between gap-3 text-sm">
                  <Badge tone={status.tone} icon={status.tone === 'green' ? '✓' : status.tone === 'yellow' ? '…' : '‖'}>{status.label}</Badge>
                  <span className="text-gray-500">Last event {formatRelative(site.last_event_at)}</span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}
